import { Box } from '@mui/material';
import React from 'react';

type DeviceType = 'mobile' | 'ipad' | 'desktop';

interface DeviceFrameProps {
    device: DeviceType;
    children: React.ReactNode;
}

const screenScrollbarSx = {
    '&::-webkit-scrollbar': { width: 4 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.18)', borderRadius: 2 },
    scrollbarWidth: 'thin' as const,
    scrollbarColor: 'rgba(255,255,255,0.18) transparent',
};

const PhoneFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box data-attr="screenshot-device-frame-phone" sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'linear-gradient(145deg, #2d2d2f, #1a1a1c)',
        borderRadius: '44px',
        p: '14px 10px',
        boxShadow: '0 25px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)',
        border: '2px solid #3a3a3c',
        position: 'relative',
        maxWidth: '100%',
    }}>
        {/* Dynamic island */}
        <Box sx={{ height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <svg width="100" height="28" viewBox="0 0 100 28" fill="none">
                <rect x="18" y="7" width="64" height="14" rx="7" fill="#0a0a0a" />
                <circle cx="36" cy="14" r="3.5" fill="#1a1a2e" opacity="0.6" />
            </svg>
        </Box>
        {/* Screen */}
        <Box sx={{
            borderRadius: '24px',
            lineHeight: 0,
            background: '#000',
            width: 280,
            maxWidth: '100%',
            aspectRatio: '9 / 19.5',
            maxHeight: 'calc(100vh - 430px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            ...screenScrollbarSx,
        }}>
            {children}
        </Box>
        {/* Home indicator */}
        <Box sx={{ height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <svg width="140" height="22" viewBox="0 0 140 22" fill="none">
                <rect x="35" y="9" width="70" height="5" rx="2.5" fill="#555" />
            </svg>
        </Box>
        {/* Volume buttons (left side) */}
        <svg style={{ position: 'absolute', left: -4, top: '18%' }} width="4" height="64" viewBox="0 0 4 64" fill="none">
            <rect y="0" width="4" height="16" rx="2" fill="#3a3a3c" />
            <rect y="24" width="4" height="28" rx="2" fill="#3a3a3c" />
            <rect y="58" width="4" height="28" rx="2" fill="#3a3a3c" />
        </svg>
        {/* Power button (right side) */}
        <svg style={{ position: 'absolute', right: -4, top: '22%' }} width="4" height="44" viewBox="0 0 4 44" fill="none">
            <rect width="4" height="44" rx="2" fill="#3a3a3c" />
        </svg>
    </Box>
);

const TabletFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box data-attr="screenshot-device-frame-tablet" sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        background: 'linear-gradient(145deg, #2d2d2f, #1a1a1c)',
        borderRadius: '24px',
        p: '18px 18px',
        boxShadow: '0 25px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)',
        border: '2px solid #3a3a3c',
        position: 'relative',
        maxWidth: '100%',
    }}>
        {/* Front camera */}
        <Box sx={{ height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <svg width="40" height="14" viewBox="0 0 40 14" fill="none">
                <circle cx="20" cy="7" r="3" fill="#1a1a2e" stroke="#333" strokeWidth="0.5" />
            </svg>
        </Box>
        {/* Screen */}
        <Box sx={{
            borderRadius: '8px',
            lineHeight: 0,
            background: '#000',
            width: 540,
            maxWidth: '100%',
            aspectRatio: '3 / 4',
            maxHeight: 'calc(100vh - 420px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            ...screenScrollbarSx,
        }}>
            {children}
        </Box>
        {/* Home indicator */}
        <Box sx={{ height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <svg width="120" height="18" viewBox="0 0 120 18" fill="none">
                <rect x="30" y="8" width="60" height="4" rx="2" fill="#555" />
            </svg>
        </Box>
        {/* Power button (top right) */}
        <svg style={{ position: 'absolute', right: -4, top: '12%' }} width="4" height="30" viewBox="0 0 4 30" fill="none">
            <rect width="4" height="30" rx="2" fill="#3a3a3c" />
        </svg>
    </Box>
);

const DesktopFrame: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Box data-attr="screenshot-device-frame-desktop" sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        maxWidth: '100%',
    }}>
        {/* Monitor body */}
        <Box sx={{
            background: 'linear-gradient(180deg, #2d2d2f, #1a1a1c)',
            borderRadius: '12px 12px 4px 4px',
            p: '10px 10px 24px 10px',
            boxShadow: '0 20px 50px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08)',
            border: '2px solid #3a3a3c',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: '100%',
        }}>
            {/* Webcam */}
            <Box sx={{ height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <svg width="30" height="12" viewBox="0 0 30 12" fill="none">
                    <circle cx="15" cy="6" r="2.5" fill="#1a1a2e" stroke="#444" strokeWidth="0.5" />
                    <circle cx="15" cy="6" r="1" fill="#2a2a4e" opacity="0.6" />
                </svg>
            </Box>
            {/* Screen */}
            <Box sx={{
                borderRadius: '4px',
                lineHeight: 0,
                background: '#000',
                width: 740,
                maxWidth: '100%',
                aspectRatio: '16 / 10',
                maxHeight: 'calc(100vh - 460px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                ...screenScrollbarSx,
            }}>
                {children}
            </Box>
            {/* Bottom chin with logo */}
            <Box sx={{ height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                <svg width="60" height="16" viewBox="0 0 60 16" fill="none">
                    <rect x="22" y="7" width="16" height="3" rx="1.5" fill="#444" />
                </svg>
            </Box>
        </Box>
        {/* Stand neck */}
        <svg width="80" height="32" viewBox="0 0 80 32" fill="none">
            <path d="M28 0 H52 L56 32 H24 Z" fill="linear-gradient(#2d2d2f, #1a1a1c)" />
            <path d="M28 0 H52 L56 32 H24 Z" fill="#2a2a2c" stroke="#3a3a3c" strokeWidth="1" />
        </svg>
        {/* Stand base */}
        <svg width="160" height="14" viewBox="0 0 160 14" fill="none">
            <ellipse cx="80" cy="7" rx="60" ry="7" fill="#2a2a2c" stroke="#3a3a3c" strokeWidth="1" />
        </svg>
    </Box>
);

const DeviceFrame: React.FC<DeviceFrameProps> = ({ device, children }) => {
    switch (device) {
        case 'mobile':
            return <PhoneFrame>{children}</PhoneFrame>;
        case 'ipad':
            return <TabletFrame>{children}</TabletFrame>;
        case 'desktop':
            return <DesktopFrame>{children}</DesktopFrame>;
    }
};

export default DeviceFrame;
